/**
 * Cliente de Pagos · Sprint 42 · Entrega 2.
 *
 * Consume los endpoints reales del backend:
 *   · POST /api/pagos/intent  (idempotente)
 *   · GET  /api/pagos/{id}    (polling)
 *
 * NUNCA emite boletos ni QR desde el frontend. La fuente de verdad
 * es el backend: `finalizar_pago_aprobado` en `services/pagos_finalizacion.py`.
 */

import { api } from "@/services/api";

export const ESTADOS_PAGO_TERMINALES = [
  "APROBADO",
  "RECHAZADO",
  "CANCELADO",
  "EXPIRADO",
  "ERROR_COMUNICACION",
];

export const esEstadoTerminal = (estado) =>
  ESTADOS_PAGO_TERMINALES.includes(estado);

export const isSimuladorHabilitado = () =>
  process.env.NODE_ENV !== "production" ||
  process.env.REACT_APP_PAYMENT_SIMULATOR === "true";

// UUIDs URL-safe compatibles con navegadores modernos (fallback simple).
export const nuevaIdempotencyKey = (prefix = "KEY") => {
  const rand =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  return `${prefix}-${rand}`.slice(0, 80);
};

/**
 * Crea la intención de pago. Idempotente por `idempotencyKey`.
 * Devuelve el objeto Pago o `{error}` con detalle del backend.
 */
export const crearIntent = async ({
  idempotencyKey,
  compraId,
  socioId,
  eventoId,
  monto,
  moneda = "MXN",
  metadatos = null,
}) => {
  try {
    const { data } = await api.post("/pagos/intent", {
      idempotencyKey,
      compraId,
      socioId,
      eventoId,
      monto: Number(monto),
      moneda,
      metadatos: metadatos || undefined,
    });
    return data;
  } catch (err) {
    return {
      error: err?.response?.data?.detail || "network_error",
      status: err?.response?.status || 0,
    };
  }
};

/**
 * Consulta el estado actual de un pago (polling).
 * Devuelve el Pago o `null` si hubo error de red (para reintentar).
 */
export const obtenerPago = async (pagoId) => {
  try {
    const { data } = await api.get(`/pagos/${pagoId}`);
    return data;
  } catch {
    return null;
  }
};

// Mensajes al socio para cada estado terminal fallido.
export const mensajeParaEstadoFallido = (pago) => {
  if (!pago) return "No pudimos procesar tu pago. Vuelve a intentarlo.";
  const motivo = pago.motivoRechazo ? ` (${pago.motivoRechazo})` : "";
  switch (pago.estado) {
    case "RECHAZADO":
      return `El banco rechazó tu pago${motivo}. No se realizó ningún cargo. Puedes reintentar con la misma tarjeta u otra distinta.`;
    case "CANCELADO":
      return "El pago fue cancelado. No se realizó ningún cargo. Puedes reintentar cuando quieras.";
    case "EXPIRADO":
      return "El tiempo para completar el pago venció. No se realizó ningún cargo. Puedes reintentar mientras conserves tu turno.";
    case "ERROR_COMUNICACION":
      return "Hubo un problema de comunicación con el banco. No se realizó ningún cargo. Reintenta en unos segundos.";
    default:
      return `Ocurrió un problema con el pago (${pago.estado}). No se realizó ningún cargo.`;
  }
};

export const RESULTADOS_SIMULADOR = [
  { id: "approved", label: "APROBADO", descripcion: "El banco autoriza el pago." },
  { id: "rejected", label: "RECHAZADO", descripcion: "El banco rechaza el pago." },
  { id: "error", label: "ERROR", descripcion: "Falla de comunicación con el banco." },
  { id: "cancelled", label: "CANCELADO", descripcion: "Cancelado por el usuario." },
  { id: "timeout", label: "TIMEOUT", descripcion: "Sin respuesta · expirará por reloj." },
];
